export type ThemeScale = Record<
  "50" | "100" | "200" | "300" | "400" | "500" | "600" | "700" | "800" | "900",
  string
>;

export interface ThemeNeutrals {
  background: string;
  surface: string;
  border: string;
  text: string;
}

// Paleta da empresa, por papel (não por matiz) — mesmo formato gravado no
// backend em gbl_settings (key "theme").
export interface TenantPalette {
  primary: ThemeScale;
  accent: ThemeScale;
  success: ThemeScale;
  warning: ThemeScale;
  light: ThemeNeutrals;
  dark: ThemeNeutrals;
}

export type BrandScales = Pick<TenantPalette, "primary" | "accent" | "success" | "warning">;

// Cor de texto/ícone de destaque (título, breadcrumb, menu, abas, sort/filtro).
// Separada de primary.dark porque primary.dark também é usado como fundo
// sólido (botões, chip de filtro ativo) — lá o tom escuro é o certo; em texto
// sobre fundo escuro ele fica fosco e força a vista, então usa uma variação
// mais clara só no modo escuro.
//
// highlight: cor de realce dos overrides globais (ícones, foco de input, aba
// selecionada, h6). Fica no palette para que os overrides não importem um
// token de marca fixo — o tema do login e o da aplicação usam o mesmo código.
//
// brand: escalas 50–900 da empresa, para as telas que precisam de um tom
// específico (theme.palette.brand.accent[500]).
declare module "@mui/material/styles" {
  interface Palette {
    accentText: string;
    highlight: string;
    brand: BrandScales;
  }
  interface PaletteOptions {
    accentText?: string;
    highlight?: string;
    brand?: BrandScales;
  }
}

// Paleta SmartTower — usada só pela tela de login (layout/auth e o tema
// `lightTheme`). Não usar dentro da aplicação: lá vale theme.palette.brand.
export const smartTowerColors = {
  blue50: "#EEF2FB",
  blue100: "#D9E3F5",
  blue300: "#8E9ED3",
  blue500: "#4258B0",
  blue600: "#36478F",
  blue800: "#1F264C",
  blue950: "#13152B",
  slateGray: "#64748B",
  iceCyan300: "#A5F3FC",
  iceCyan500: "#38BDF8",
  success: "#10B981",
  successHover: "#059669",
  successTint: "rgba(16, 185, 129, 0.14)",
  warning: "#F59E0B",
  warningHover: "#D97706",
  warningTint: "rgba(245, 158, 11, 0.16)",
  danger: "#EF4444",
  dangerHover: "#DC2626",
  dangerTint: "rgba(239, 68, 68, 0.14)",
  info: "#38BDF8",
  infoTint: "rgba(56, 189, 248, 0.16)",
} as const;

// Erro não faz parte da marca: fica igual para todas as empresas.
export const dangerColors = {
  main: "#EF4444",
  hover: "#DC2626",
} as const;

// Paleta padrão, usada enquanto a da empresa não chega (ou se ela não tiver
// uma cadastrada): SmartTower, o visual do produto. A marca de cada empresa
// (ex.: HTE) vem só do banco dela, em gbl_settings.
//
// Escala azul do sistema, ancorada nas três cores oficiais:
// 100 = azul claro #D9E3F5, 500 = azulada #4258B0, 900 = azul forte #13152B.
export const defaultTenantPalette: TenantPalette = {
  primary: {
    50: "#EEF2FB", 100: "#D9E3F5", 200: "#B3C0E4", 300: "#8E9ED3", 400: "#687BC1",
    500: "#4258B0", 600: "#36478F", 700: "#2B376E", 800: "#1F264C", 900: "#13152B",
  },
  accent: {
    50: "#EEF2FB", 100: "#D9E3F5", 200: "#B3C0E4", 300: "#8E9ED3", 400: "#687BC1",
    500: "#4258B0", 600: "#36478F", 700: "#2B376E", 800: "#1F264C", 900: "#13152B",
  },
  success: {
    50: "#ECFDF5", 100: "#D1FAE5", 200: "#A7F3D0", 300: "#6EE7B7", 400: "#34D399",
    500: "#10B981", 600: "#059669", 700: "#047857", 800: "#065F46", 900: "#064E3B",
  },
  warning: {
    50: "#FFFBEB", 100: "#FEF3C7", 200: "#FDE68A", 300: "#FCD34D", 400: "#FBBF24",
    500: "#F59E0B", 600: "#D97706", 700: "#B45309", 800: "#92400E", 900: "#78350F",
  },
  light: { background: "#FAFBFF", surface: "#F2F6FA", border: "#E0E0E0", text: "#2A2B2F" },
  dark: { background: "#050505", surface: "#121212", border: "#2A2A2A", text: "#FFFFFF" },
};
